<template>
  <div class="page-header">
    <h2>Gestion des Trajets</h2>
    <button @click="openCreateModal" class="btn btn-primary">+ Créer un trajet</button>
  </div>
  <!-- Messages d'info -->
  <InfoBox
    v-if="infoMessage"
    v-model:open="infoOpen"
    :type="infoType"
    @close="infoOpen = false; infoMessage = ''"
  >
    {{ infoMessage }}
  </InfoBox>
  <!-- Filtres -->
  <section class="filters form-group">
    <div class="btn-group">
      <div class="filters-group">
        <label>Date de départ</label>
        <input v-model="filters.date" type="date" class="input" />
      </div>
      <div class="filters-group">
        <label>Adresse de départ</label>
        <input v-model="filters.addressStart" type="text" placeholder="Rechercher..." />
      </div>
      <div class="filters-group">
        <label>Adresse d'arrivée</label>
        <input v-model="filters.addressEnd" type="text" placeholder="Rechercher..." />
      </div>
      <div class="filters-group">
        <label>Prix min (FCFA)</label>
        <input v-model.number="filters.priceMin" type="number" placeholder="0" step="1" />
      </div>
      <div class="filters-group">
        <label>Prix max (FCFA)</label>
        <input v-model.number="filters.priceMax" type="number" placeholder="999999" step="1" />
      </div>
      <div class="filters-group">
        <label>Chauffeur</label>
        <input v-model="filters.driver" type="text" placeholder="Rechercher..." />
      </div>
      <div class="filters-group">
        <label>Véhicule</label>
        <input v-model="filters.vehicle" type="text" placeholder="Rechercher..." />
      </div>
    </div>
    <div class="btn-group">
      <span class="badge badge-info">
        {{ filteredTrips.length }} trajet(s) trouvé(s) sur {{ allTrips.length }}
      </span>
      <button @click="resetFilters" class="btn btn-secondary">Réinitialiser</button>
    </div>
  </section>
  <!-- Tableau des trajets -->
  <table v-if="!loading && allTrips.length > 0" class="table">
    <thead>
      <tr>
        <th @click="sortBy('addressToStart')" class="th--sortable">Départ <span class="sort-icon">{{ getSortIcon('addressToStart') }}</span></th>
        <th @click="sortBy('addressToEnd')" class="th--sortable">Arrivée <span class="sort-icon">{{ getSortIcon('addressToEnd') }}</span></th>
        <th @click="sortBy('timeToStart')" class="th--sortable">Heure <span class="sort-icon">{{ getSortIcon('timeToStart') }}</span></th>
        <th @click="sortBy('price')" class="th--sortable">Prix <span class="sort-icon">{{ getSortIcon('price') }}</span></th>
        <th @click="sortBy('vehicle')" class="th--sortable">Véhicule <span class="sort-icon">{{ getSortIcon('vehicle') }}</span></th>
        <th @click="sortBy('driver')" class="th--sortable">Chauffeur <span class="sort-icon">{{ getSortIcon('driver') }}</span></th>
        <th @click="sortBy('availablePlaces')" class="th--sortable">Places <span class="sort-icon">{{ getSortIcon('availablePlaces') }}</span></th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="trip in visibleTrips" :key="trip.uuid" class="row">
        <td>{{ trip.addressToStart?.name }}</td>
        <td>{{ trip.addressToEnd?.name }}</td>
        <td>{{ formatDateTime(trip.timeToStart) }}</td>
        <td class="price">{{ trip.price }} FCFA</td>
        <td>{{ trip.vehicle?.model }} - {{ trip.vehicle?.noImmat }}</td>
        <td>{{ trip.driver?.firstName }} {{ trip.driver?.lastName }}</td>
        <td><span class="badge badge-neutral">{{ trip.availablePlaces }}/{{ trip.vehicle?.nbPlaces }}</span></td>
        <td>
          <button @click="openEditModal(trip)" class="icon-btn icon-btn--edit" title="Éditer">✏️</button>
          <button @click="openDeleteModal(trip)" class="icon-btn danger" title="Supprimer">🗑</button>
        </td>
      </tr>
    </tbody>
  </table>
  <!-- États vides -->
  <EmptyState
    v-if="!loading && filteredTrips.length === 0 && allTrips.length > 0"
    :is-empty="true"
    emptyMessage="Aucun trajet ne correspond aux filtres"
  />
  <!-- Bouton Charger plus -->
  <div v-if="!loading && visibleTrips.length > 0 && visibleCount < filteredTrips.length"
    class="btn-group center">
    <button @click="loadMore" class="btn">- Charger plus -</button>
  </div>
</template>

<script setup>
import { config } from '@/config/env.js'
import { ref, onMounted, computed } from 'vue'
import { useModalStore } from '@/stores/modalStore'
import { useToast } from 'vue-toast-notification'
import FormFieldsTrip from '@/components/FormFields/FormFieldsTrip.vue'
import InfoBox from '@/components/InfoBox.vue'
import EmptyState from '@/components/EmptyState.vue'

const emit = defineEmits(['loading'])
const modalStore = useModalStore()
const toast = useToast()
const infoOpen = ref(false)
const infoType = ref('info')
const infoMessage = ref('')

const allTrips = ref([])
const allVehicles = ref([])
const allAddresses = ref([])
const allDrivers = ref([])
const loading = ref(false)
const visibleCount = ref(10)
const ITEMS_STEP = 10

const sortKey = ref('timeToStart')
const sortOrder = ref('desc')

const filters = ref({
  date: '',
  addressStart: '',
  addressEnd: '',
  priceMin: null,
  priceMax: null,
  vehicle: '',
  driver: ''
})

const resetFilters = () => {
  filters.value = {
    date: '',
    addressStart: '',
    addressEnd: '',
    priceMin: null,
    priceMax: null,
    vehicle: ''
  }
  visibleCount.value = ITEMS_STEP
}

const filteredTrips = computed(() => {
  return allTrips.value.filter(trip => {
    if (filters.value.date) {
      const tripDate = new Date(trip.timeToStart).toISOString().split('T')[0]
      if (tripDate !== filters.value.date) return false
    }

    if (filters.value.addressStart) {
      const searchTerm = filters.value.addressStart.toLowerCase()
      const addressName = trip.addressToStart?.name?.toLowerCase() || ''
      if (!addressName.includes(searchTerm)) return false
    }

    if (filters.value.addressEnd) {
      const searchTerm = filters.value.addressEnd.toLowerCase()
      const addressName = trip.addressToEnd?.name?.toLowerCase() || ''
      if (!addressName.includes(searchTerm)) return false
    }

    if (filters.value.priceMin !== null && filters.value.priceMin !== '') {
      if (trip.price < filters.value.priceMin) return false
    }

    if (filters.value.priceMax !== null && filters.value.priceMax !== '') {
      if (trip.price > filters.value.priceMax) return false
    }

    if (filters.value.vehicle) {
      const searchTerm = filters.value.vehicle.toLowerCase()
      const vehicleInfo = `${trip.vehicle?.model || ''} ${trip.vehicle?.noImmat || ''}`.toLowerCase()
      if (!vehicleInfo.includes(searchTerm)) return false
    }

    if (filters.value.driver) {
      const searchTerm = filters.value.driver.toLowerCase()
      const driverName = `${trip.driver?.firstName || ''} ${trip.driver?.lastName || ''}`.toLowerCase()
      if (!driverName.includes(searchTerm)) return false
    }

    return true
  })
})

const sortedTrips = computed(() => {
  const trips = [...filteredTrips.value]
  trips.sort((a, b) => {
    let aVal, bVal
    switch (sortKey.value) {
      case 'addressToStart': aVal = a.addressToStart?.name || ''; bVal = b.addressToStart?.name || ''; break
      case 'addressToEnd': aVal = a.addressToEnd?.name || ''; bVal = b.addressToEnd?.name || ''; break
      case 'timeToStart': aVal = new Date(a.timeToStart); bVal = new Date(b.timeToStart); break
      case 'price': aVal = a.price || 0; bVal = b.price || 0; break
      case 'vehicle': aVal = a.vehicle?.model || ''; bVal = b.vehicle?.model || ''; break
      case 'driver':
        aVal = `${a.driver?.firstName || ''} ${a.driver?.lastName || ''}`.trim()
        bVal = `${b.driver?.firstName || ''} ${b.driver?.lastName || ''}`.trim()
        break
      case 'availablePlaces': aVal = a.availablePlaces || 0; bVal = b.availablePlaces || 0; break
      default: return 0
    }
    if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1
    return 0
  })
  return trips
})

const visibleTrips = computed(() => sortedTrips.value.slice(0, visibleCount.value))

const sortBy = (key) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const getSortIcon = (key) => sortKey.value !== key ? '⇅' : (sortOrder.value === 'asc' ? '↑' : '↓')

const loadMore = () => {
  if (visibleCount.value < sortedTrips.value.length) {
    visibleCount.value += ITEMS_STEP
  }
}

onMounted(async () => {
  await Promise.all([loadTrips(), loadVehicles(), loadAddresses(), loadDrivers()])
})

const loadTrips = async () => {
  loading.value = true
  emit('loading', true)
  try {
    const res = await fetch(config.apiUrl + '/trips', { credentials: 'include' })
    if (res.ok) {
      allTrips.value = await res.json()
      visibleCount.value = ITEMS_STEP
    } else {
      showInfo('error', 'Erreur lors du chargement des trajets')
      toast.error('Erreur lors du chargement des trajets')
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const loadVehicles = async () => {
  try {
    const res = await fetch(config.apiUrl + '/vehicles', { credentials: 'include' })
    if (res.ok) {
      allVehicles.value = await res.json()
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur lors du chargement des véhicules')
  }
}

const loadAddresses = async () => {
  try {
    const res = await fetch(config.apiUrl + '/addresses', { credentials: 'include' })
    if (res.ok) {
      allAddresses.value = await res.json()
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur lors du chargement des adresses')
  }
}

const loadDrivers = async () => {
  try {
    const res = await fetch(config.apiUrl + '/users/drivers', { credentials: 'include' })
    if (res.ok) {
      allDrivers.value = await res.json()
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur lors du chargement des chauffeurs')
  }
}

// 🎯 CRÉATION
const openCreateModal = async () => {
  const { result } = modalStore.openComponent(
    FormFieldsTrip,
    {
      formData: {
        vehicleId: '',
        driverId: '',
        addressIdToStart: '',
        addressIdToEnd: '',
        timeToStart: '',
        timeToEnd: '',
        price: ''
      },
      vehicles: allVehicles.value,
      addresses: allAddresses.value,
      drivers: allDrivers.value,
      isEdit: false
    },
    { title: 'Créer un trajet', maxWidth: '700px', kind: 'default' }
  )
  const res = await result
  if (res.action === 'submit') await handleSaveTrip(res.data, false)
}

// 🎯 ÉDITION
const openEditModal = async (trip) => {
  const { result } = modalStore.openComponent(
    FormFieldsTrip,
    {
      formData: {
        vehicleId: trip.vehicle?.id || '',
        driverId: trip.driver?.id || '',
        addressIdToStart: trip.addressToStart?.id || '',
        addressIdToEnd: trip.addressToEnd?.id || '',
        timeToStart: convertToDatetimeLocal(trip.timeToStart),
        timeToEnd: convertToDatetimeLocal(trip.timeToEnd),
        price: trip.price || ''
      },
      drivers: allDrivers.value,
      vehicles: allVehicles.value,
      addresses: allAddresses.value,
      isEdit: true
    },
    { title: 'Modifier le trajet', maxWidth: '700px', kind: 'default' }
  )
  const res = await result
  if (res.action === 'submit') await handleSaveTrip(res.data, true, trip.uuid)
}

const handleSaveTrip = async (formData, isEdit, uuid = null) => {
  loading.value = true
  emit('loading', true)

  try {
    const isEditMode = !!isEdit
    const url = isEditMode
      ? `${config.apiUrl}/trip/${uuid}`
      : `${config.apiUrl}/trip/create`

    const res = await fetch(url, {
      method: isEditMode ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(formData)
    })

    const successMsg = isEditMode ? 'Trajet modfié avec succès' : 'Trajet créé avec succès'

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}))
      throw new Error(errData.message || `Erreur ${res.status}`)
    }

    await loadTrips()
    showInfo('success', successMsg)
    toast.success(successMsg)

  } catch (err) {
    const msg = err?.message || 'Erreur de connexion'
    showInfo('error', msg)
    toast.error(msg)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const openDeleteModal = async (trip) => {
  const { result } = modalStore.open({
    kind: 'confirm',
    title: 'Confirmer la suppression',
    message: `Êtes-vous sûr de vouloir supprimer le trajet ${trip.addressToStart?.name} → ${trip.addressToEnd?.name} ?`,
    confirmText: 'Supprimer',
    cancelText: 'Annuler'
  })
  const res = await result
  if (res.action === 'confirm') await handleDeleteTrip(trip)
}

const handleDeleteTrip = async (trip) => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(`${config.apiUrl}/trip/${trip.uuid}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (res.ok) {
      await loadTrips()
      toast.success('Trajet supprimé avec succès')
    } else {
      showInfo('error', 'Erreur lors de la suppression')
      toast.error('Erreur lors de la suppression')
    }
  } catch {
    const errorMsg = 'Erreur de connexion'
    showInfo('error', errorMsg)
    toast.error(errorMsg)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const formatDateTime = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR') + ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

const convertToDatetimeLocal = (dateString) => {
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

const showInfo = (type, message) => {
  infoType.value = type
  infoMessage.value = message
  infoOpen.value = true
}
</script>
